


































































































































import { defineComponent, computed, ref, watch } from '@vue/composition-api';
import debounce from 'lodash/debounce';
import { AxiosError } from 'axios';
import store from '@/store';
import { snackbar } from '@/components/GlobalSnackbar';
import { UpdateUserPayload, User } from '@/types';
import { updateUserMenu, resetUserPasswordMenu } from '../state';
import ResetUserPasswordMenu from './ResetUserPasswordMenu.vue';
import { useClient } from '@/use';

export default defineComponent({
  name: 'UpdateUser',
  components: { ResetUserPasswordMenu },
  setup() {
    const client = useClient();
    const institutions = computed(
      () => store.state.application.applicationData?.lists.institutions,
    );
    const userLevels = computed(() => (
      store.state.application.applicationData?.lists
      .user_types?.map((val, i) => `${i} (${val})`) || []
    ));

    const formValid = ref(false);
    const commonRules = [
      (val: string | number | null) => (val !== '' && val !== null),
    ];

    const allUsers = ref<User[]>([]);
    const currentUser = ref<User | null>(null);
    const userSearch = ref('');
    const loadingUsers = ref(false);

    // Editable values
    // const institution = ref<string | null>(null);
    const accessibleInstitutions = ref<string[]>([]);
    const usergroup = ref<string>('');
    const email = ref<string>('');
    const emailVerified = ref(false);
    const userlevel = ref<number | null>(null);
    const selectedUserLevel = computed(() => (
      userlevel.value === null ? null : userLevels.value[userlevel.value] || null
    ));
    function setUserLevel(levelStr: string) {
      const level = userLevels.value?.findIndex((str) => levelStr === str);

      if (level !== undefined && level >= 0) {
        userlevel.value = level;
      }
    }

    async function searchUsers() {
      loadingUsers.value = true;
      try {
        allUsers.value = await client.fetchUsers(userSearch.value, 25);
      } finally {
        loadingUsers.value = false;
      }
    }
    watch(updateUserMenu, (open) => {
      if (open) searchUsers();
    });
    watch(userSearch, debounce(searchUsers, 250));

    // If user changed set current list of institutions
    watch(currentUser, (user) => {
      if (user === null) {
        return;
      }

      // institution.value = user.institution;
      email.value = user.email;
      emailVerified.value = user.email_verified === true;
      usergroup.value = user.user_group;
      accessibleInstitutions.value = user.accessible_institutions;
      userlevel.value = user.user_level;
    });

    function closeMenu() { updateUserMenu.value = false; }
    async function updateUser() {
      if (
        !currentUser.value
        || userlevel.value === null
      ) {
        throw new Error('Null values when updating user!');
      }

      const payload: UpdateUserPayload = {
        accessible_institutions: accessibleInstitutions.value,
        user_level: userlevel.value,
        user_group: usergroup.value,
        email: email.value,
        email_verified: emailVerified.value,
      };

      const { username } = currentUser.value;
      await client.updateUser(username, payload);
      snackbar.dispatch({ text: `User ${username} successfully updated.` });

      closeMenu();
    }

    async function deleteUser() {
      if (!currentUser.value) {
        return;
      }

      const { username } = currentUser.value;
      try {
        await client.removeUser(username);
        snackbar.dispatch({
          text: `User ${username} successfully deleted.`,
          options: {
            top: true,
            color: 'success',
          },
        });
        closeMenu();
      } catch (err) {
        const error = err as AxiosError;
        snackbar.dispatch({
          text: error.response?.data || 'Unknown error, please check the console.',
          options: {
            color: 'error',
            top: true,
          },
        });
      }
    }

    return {
      resetUserPasswordMenu,
      userLevels,
      userlevel,
      usergroup,
      setUserLevel,
      currentUser,
      allUsers,
      userSearch,
      loadingUsers,
      formValid,
      commonRules,
      email,
      emailVerified,
      selectedUserLevel,
      // institution,
      accessibleInstitutions,
      institutions,
      closeMenu,
      updateUser,
      deleteUser,
    };
  },
});
